<template>
  <div class="wtg">
    <!-- toolbar -->
    <div class="wtg-bar">
      <b-input-group size="sm" class="wtg-search">
        <b-input-group-prepend is-text><i class="fas fa-search"/></b-input-group-prepend>
        <b-form-input v-model="search" :placeholder="$t('whatsappHub.search')" debounce="150"/>
      </b-input-group>
      <div class="wtg-seg">
        <button v-for="s in statusFilters" :key="s" type="button" class="wtg-seg__btn" :class="{'wtg-seg__btn--on': statusFilter === s}" @click="statusFilter = s">
          {{ $t('whatsappHub.templates.filters.' + s) }}<span v-if="s !== 'all'" class="wtg-seg__n tabular">{{ countBy(s) }}</span>
        </button>
      </div>
      <b-form-select v-model="langFilter" size="sm" :options="languageOptions" class="wtg-lang"/>
      <span class="wtg-bar__spacer"/>
      <button type="button" class="wtg-tool" :class="{'wtg-tool--on': showCapabilities}" v-b-tooltip.hover :title="$t('whatsappHub.capabilities.title')" @click="showCapabilities = !showCapabilities">
        <i class="fas fa-circle-question"/>
      </button>
      <button type="button" class="wtg-tool" v-b-tooltip.hover :title="$t('whatsappHub.reload')" @click="refresh(true)">
        <i class="fas fa-redo" :class="{'fa-spin': loading}"/>
      </button>
      <b-button variant="success" size="sm" :disabled="!canCreate" v-b-tooltip.hover :title="canCreate ? '' : $t('whatsappHub.templates.twilioOnly')" @click="$refs.builder.open()">
        <i class="fas fa-plus mr-25"/>{{ $t('whatsappHub.templates.new') }}
      </b-button>
    </div>

    <!-- what Twilio WhatsApp can do -->
    <transition name="wtg-fade">
      <div v-if="showCapabilities" class="wtg-caps">
        <div class="wtg-caps__head">
          <i class="fab fa-whatsapp"/><b>{{ $t('whatsappHub.capabilities.title') }}</b>
          <span class="wtg-caps__sub">{{ $t('whatsappHub.capabilities.subtitle') }}</span>
        </div>
        <div class="wtg-caps__grid">
          <div v-for="cap in capabilities" :key="cap.key" class="wtg-cap" :class="{'wtg-cap--soon': cap.soon}">
            <i :class="cap.icon"/>
            <b>{{ $t('whatsappHub.capabilities.' + cap.key + '.title') }}</b>
            <span>{{ $t('whatsappHub.capabilities.' + cap.key + '.text') }}</span>
          </div>
        </div>
      </div>
    </transition>

    <div v-if="status && status.provider === 'META'" class="wtg-note"><i class="fas fa-circle-info mr-50"/>{{ $t('whatsappHub.templates.metaNote') }}</div>

    <!-- grid -->
    <div v-if="loading && templates.length === 0" class="wtg-empty"><b-spinner small class="mr-50"/>{{ $t('whatsappHub.composer.loadingTemplates') }}</div>
    <div v-else-if="error" class="wtg-error"><i class="fas fa-triangle-exclamation mr-50"/>{{ error }}</div>
    <div v-else-if="visible.length === 0" class="wtg-empty">
      <i class="fas fa-file-lines"/>
      <p>{{ $t('whatsappHub.empty.templates') }}</p>
      <b-button v-if="canCreate && templates.length === 0" variant="outline-success" size="sm" @click="$refs.builder.open()">
        <i class="fas fa-plus mr-25"/>{{ $t('whatsappHub.templates.new') }}
      </b-button>
    </div>
    <div v-else class="wtg-grid">
      <div v-for="tpl in visible" :key="tpl.sid" class="wtg-card" :data-tone="approvalTone(tpl.approvalStatus)">
        <div class="wtg-card__head">
          <div class="wtg-card__names">
            <span class="wtg-card__name" :title="tpl.name">{{ tpl.name }}</span>
            <span class="wtg-card__meta">
              <span><i class="fas fa-language"/>{{ tpl.language }}</span>
              <span><i :class="typeIcon(tpl.type)"/>{{ $t('whatsappHub.builder.types.' + typeKey(tpl.type)) }}</span>
              <span v-if="tpl.category"><i class="fas fa-folder-open"/>{{ tpl.category }}</span>
              <span v-if="variableCount(tpl)"><i class="fas fa-tag"/>{{ variableCount(tpl) }}</span>
            </span>
          </div>
          <span class="wtg-status" :data-tone="approvalTone(tpl.approvalStatus)" v-b-tooltip.hover :title="tpl.rejectionReason || ''">
            <i/>{{ $t('whatsappHub.templates.statuses.' + (tpl.approvalStatus || 'unsubmitted')) }}
          </span>
        </div>

        <whatsapp-phone-preview compact :type="tpl.type" :body="tpl.body || ''" :header="tpl.header || ''" :footer="tpl.footer || ''"
                                :title="tpl.title || ''" :subtitle="tpl.subtitle || ''" :button="tpl.button || ''"
                                :media-urls="tpl.mediaUrls || []" :actions="tpl.actions || []" :items="tpl.items || []"
                                :variables="tpl.variables" :sender="status ? status.branchName : ''"
                                :empty-text="$t('whatsappHub.templates.noBody')" class="wtg-card__preview"/>

        <div v-if="tpl.rejectionReason" class="wtg-card__reason"><i class="fas fa-triangle-exclamation mr-25"/>{{ tpl.rejectionReason }}</div>

        <div class="wtg-card__foot">
          <span class="wtg-card__date tabular" v-if="tpl.dateUpdated">{{ tpl.dateUpdated | moment('DD.MM.YY') }}</span>
          <span class="wtg-bar__spacer"/>
          <button v-if="isTwilio(tpl) && (tpl.approvalStatus === 'unsubmitted' || tpl.approvalStatus === 'rejected')" type="button" class="wtg-action wtg-action--submit"
                  v-b-tooltip.hover :title="$t('whatsappHub.templates.submit')" @click="askSubmit(tpl)"><i class="fas fa-paper-plane"/></button>
          <button v-if="isTwilio(tpl) && (tpl.approvalStatus === 'pending' || tpl.approvalStatus === 'received')" type="button" class="wtg-action"
                  v-b-tooltip.hover :title="$t('whatsappHub.templates.refreshStatus')" @click="refreshOne(tpl)"><i class="fas fa-rotate" :class="{'fa-spin': refreshing === tpl.sid}"/></button>
          <button v-if="isTwilio(tpl)" type="button" class="wtg-action wtg-action--danger" v-b-tooltip.hover :title="$t('whatsappHub.templates.delete')" @click="remove(tpl)"><i class="far fa-trash-can"/></button>
          <b-button variant="success" size="sm" :disabled="tpl.approvalStatus !== 'approved' && tpl.provider !== 'META'" class="ml-50"
                    v-b-tooltip.hover :title="tpl.approvalStatus !== 'approved' && tpl.provider !== 'META' ? $t('whatsappHub.composer.notApproved') : ''"
                    @click="$emit('use', tpl)">
            <i class="fas fa-share mr-25"/>{{ $t('whatsappHub.templates.use') }}
          </b-button>
        </div>
      </div>
    </div>

    <!-- submit for approval -->
    <b-modal v-model="submitDialog.show" centered :title="$t('whatsappHub.templates.submitTitle')" :ok-title="$t('whatsappHub.templates.submit')"
             :cancel-title="$t('whatsappHub.composer.close')" ok-variant="success" :ok-disabled="!submitDialog.name || submitDialog.busy" @ok.prevent="doSubmit">
      <b-form-group :label="$t('whatsappHub.templates.waName')" :description="$t('whatsappHub.templates.waNameHint')">
        <b-form-input v-model="submitDialog.name" @input="submitDialog.name = slug(submitDialog.name)"/>
      </b-form-group>
      <b-form-group :label="$t('whatsappHub.templates.category')">
        <b-form-radio-group v-model="submitDialog.category" stacked>
          <b-form-radio v-for="c in ['UTILITY', 'MARKETING', 'AUTHENTICATION']" :key="c" :value="c">
            <b>{{ c }}</b> <small class="text-muted">— {{ $t('whatsappHub.templates.categories.' + c) }}</small>
          </b-form-radio>
        </b-form-radio-group>
      </b-form-group>
      <small class="text-muted">{{ $t('whatsappHub.templates.approvalNote') }}</small>
    </b-modal>

    <whatsapp-template-builder ref="builder" :buid="buid" :status="status" @created="onCreated"/>
  </div>
</template>

<script>
import {mapActions, mapGetters} from 'vuex'
import WhatsappPhonePreview from './WhatsappPhonePreview.vue'
import WhatsappTemplateBuilder from './WhatsappTemplateBuilder.vue'
import {approvalTone, collectVariables, TEMPLATE_TYPES, typeKey} from './whatsappUtils'

/**
 * The branch's template library — Twilio Content with WhatsApp approval
 * state (or Meta templates, read-only) — plus the builder for new ones.
 */
export default {
  name: 'WhatsappTemplateGallery',
  components: {WhatsappPhonePreview, WhatsappTemplateBuilder},
  props: {
    buid: {type: [Number, String], default: null},
    status: {type: Object, default: null},
  },
  data: () => ({
    loading: false,
    error: null,
    search: '',
    statusFilter: 'all',
    statusFilters: ['all', 'approved', 'pending', 'rejected', 'unsubmitted'],
    langFilter: 'all',
    refreshing: null,
    showCapabilities: false,
    submitDialog: {show: false, tpl: null, name: '', category: 'UTILITY', busy: false},
  }),
  computed: {
    ...mapGetters('whatsapp', {templatesOf: 'hubTemplates'}),
    templates() {
      return this.templatesOf(this.buid) || [];
    },
    canCreate() {
      return this.status != null && this.status.canCreateTemplates;
    },
    languageOptions() {
      const langs = Array.from(new Set(this.templates.map(t => t.language).filter(Boolean)));
      return [{value: 'all', text: this.$t('whatsappHub.composer.allLanguages')}, ...langs.map(l => ({value: l, text: l}))];
    },
    visible() {
      const q = (this.search || '').trim().toLowerCase();
      return this.templates.filter(t => {
        if (this.statusFilter !== 'all' && this.normStatus(t) !== this.statusFilter) return false;
        if (this.langFilter !== 'all' && t.language !== this.langFilter) return false;
        if (q === '') return true;
        return [t.name, t.body, t.title, t.category, t.type].filter(Boolean).join(' ').toLowerCase().includes(q);
      });
    },
    capabilities() {
      return [
        {key: 'text', icon: 'fas fa-align-left'},
        {key: 'media', icon: 'fas fa-image'},
        {key: 'quickReply', icon: 'fas fa-reply'},
        {key: 'cta', icon: 'fas fa-arrow-up-right-from-square'},
        {key: 'list', icon: 'fas fa-list-ul'},
        {key: 'card', icon: 'far fa-id-card'},
        {key: 'location', icon: 'fas fa-location-dot'},
        {key: 'auth', icon: 'fas fa-key'},
        {key: 'variables', icon: 'fas fa-tag'},
        {key: 'session', icon: 'fas fa-hourglass-half'},
        {key: 'receipts', icon: 'fas fa-check-double'},
        {key: 'inbound', icon: 'fas fa-arrow-down'},
        {key: 'bulk', icon: 'fas fa-users'},
        {key: 'schedule', icon: 'far fa-clock'},
        {key: 'carousel', icon: 'fas fa-images', soon: true},
      ];
    },
  },
  watch: {
    buid() {
      this.refresh(false);
    },
  },
  created() {
    this.refresh(false);
  },
  methods: {
    ...mapActions('whatsapp', ['hubTemplates', 'hubTemplate', 'hubSubmitTemplate', 'hubDeleteTemplate']),
    approvalTone, typeKey,
    refresh(force) {
      if (!this.buid) return;
      this.loading = true;
      this.error = null;
      this.hubTemplates({buid: this.buid, force}).catch(error => this.error = error).finally(() => this.loading = false);
    },
    normStatus(t) {
      const s = (t.approvalStatus || 'unsubmitted').toLowerCase();
      if (s === 'received') return 'pending';
      if (t.provider === 'META' && s === 'approved') return 'approved';
      return ['approved', 'pending', 'rejected', 'unsubmitted'].includes(s) ? s : 'unsubmitted';
    },
    countBy(status) {
      return this.templates.filter(t => this.normStatus(t) === status).length;
    },
    isTwilio(tpl) {
      return tpl.provider !== 'META';
    },
    typeIcon(twilioType) {
      const found = TEMPLATE_TYPES.find(t => t.twilio === twilioType);
      return found ? found.icon : (twilioType === 'twilio/carousel' ? 'fas fa-images' : 'fas fa-align-left');
    },
    variableCount(tpl) {
      return collectVariables(tpl).length;
    },
    slug(name) {
      return String(name || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+/, '');
    },
    askSubmit(tpl) {
      this.submitDialog = {show: true, tpl, name: this.slug(tpl.name.replace(/\s*\(.*\)$/, '')), category: tpl.category || 'UTILITY', busy: false};
    },
    doSubmit() {
      const d = this.submitDialog;
      d.busy = true;
      this.hubSubmitTemplate({buid: this.buid, sid: d.tpl.sid, name: d.name, category: d.category}).then(() => {
        d.show = false;
        this.refreshOne(d.tpl);
      }).catch(() => {
      }).finally(() => d.busy = false);
    },
    refreshOne(tpl) {
      this.refreshing = tpl.sid;
      this.hubTemplate({buid: this.buid, sid: tpl.sid}).catch(error => {
        this.$store.dispatch('notifications/error', {title: {text: 'WhatsApp'}, message: error});
      }).finally(() => this.refreshing = null);
    },
    remove(tpl) {
      this.$bvModal.msgBoxConfirm(this.$t('whatsappHub.templates.deleteConfirm', {name: tpl.name}), {
        title: this.$t('whatsappHub.templates.delete'), okVariant: 'danger', okTitle: this.$t('whatsappHub.templates.delete'),
        cancelTitle: this.$t('whatsappHub.composer.close'), centered: true,
      }).then(ok => {
        if (ok) this.hubDeleteTemplate({buid: this.buid, sid: tpl.sid}).catch(() => {});
      });
    },
    onCreated() {
      this.statusFilter = 'all';
    },
  }
}
</script>

<style lang="scss" scoped>
.tabular { font-variant-numeric: tabular-nums; }
.wtg-bar { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; margin-bottom: 12px; }
.wtg-bar__spacer { flex: 1; }
.wtg-search { width: 220px; }
.wtg-lang { width: 110px; }
.wtg-seg { display: inline-flex; border: 1px solid rgba(15, 23, 42, .1); border-radius: 10px; overflow: hidden; background: #fff; }
.wtg-seg__btn {
  border: none; background: transparent; padding: 6px 10px; cursor: pointer; display: inline-flex; align-items: center; gap: 5px;
  font-size: .7rem; font-weight: 800; text-transform: uppercase; letter-spacing: .04em; color: #64748b;
  &:focus { outline: none; }
  & + & { border-left: 1px solid rgba(15, 23, 42, .08); }
  &--on { background: #1f2a44; color: #fff; .wtg-seg__n { background: rgba(255, 255, 255, .2); color: #fff; } }
}
.wtg-seg__n { font-size: .62rem; padding: 1px 6px; border-radius: 999px; background: rgba(15, 23, 42, .08); color: #64748b; }
.wtg-tool {
  width: 30px; height: 30px; border: none; border-radius: 9px; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center; background: rgba(15, 23, 42, .05); color: #64748b; font-size: .74rem;
  &:focus { outline: none; }
  &--on { background: rgba(37, 211, 102, .16); color: #1f9d57; }
}
.wtg-note { font-size: .78rem; color: #c77400; background: rgba(255, 159, 67, .1); border-radius: 10px; padding: 8px 12px; margin-bottom: 12px; }
.wtg-error { color: #ea5455; font-size: .82rem; font-weight: 600; padding: 20px 0; }
.wtg-empty { text-align: center; padding: 48px 0 30px; color: #94a3b8; i { font-size: 2.4rem; opacity: .35; } p { margin: 8px 0 12px; font-size: .86rem; } }

/* capabilities */
.wtg-fade-enter-active, .wtg-fade-leave-active { transition: all .2s ease; }
.wtg-fade-enter, .wtg-fade-leave-to { opacity: 0; transform: translateY(-6px); }
.wtg-caps { border: 1px solid rgba(37, 211, 102, .35); background: rgba(37, 211, 102, .05); border-radius: 14px; padding: 14px 16px; margin-bottom: 14px; }
.wtg-caps__head { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; i { color: #25d366; font-size: 1.2rem; } b { font-size: .9rem; color: #1f2a44; } }
.wtg-caps__sub { font-size: .74rem; color: #64748b; }
.wtg-caps__grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 8px; }
.wtg-cap {
  display: flex; flex-direction: column; gap: 2px; background: #fff; border-radius: 10px; padding: 10px 12px; border: 1px solid rgba(15, 23, 42, .06);
  i { color: #1f9d57; font-size: .9rem; margin-bottom: 4px; }
  b { font-size: .76rem; color: #1f2a44; }
  span { font-size: .68rem; color: #64748b; line-height: 1.35; }
  &--soon { opacity: .6; }
}

/* grid */
.wtg-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 12px; }
.wtg-card {
  display: flex; flex-direction: column; gap: 10px; background: #fff; border: 1px solid rgba(15, 23, 42, .08); border-radius: 14px; padding: 12px 14px;
  border-top: 3px solid #cbd5e1; transition: box-shadow .15s;
  &:hover { box-shadow: 0 8px 24px rgba(15, 23, 42, .08); }
  &[data-tone="ok"] { border-top-color: #28c76f; }
  &[data-tone="wait"] { border-top-color: #ff9f43; }
  &[data-tone="bad"] { border-top-color: #ea5455; }
}
.wtg-card__head { display: flex; align-items: flex-start; justify-content: space-between; gap: 8px; }
.wtg-card__names { display: flex; flex-direction: column; min-width: 0; gap: 3px; }
.wtg-card__name { font-size: .86rem; font-weight: 800; color: #1f2a44; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.wtg-card__meta { display: flex; flex-wrap: wrap; gap: 8px; font-size: .64rem; color: #94a3b8; i { margin-right: 3px; } }
.wtg-status {
  display: inline-flex; align-items: center; gap: 5px; white-space: nowrap; flex-shrink: 0; cursor: default;
  font-size: .6rem; font-weight: 800; text-transform: uppercase; letter-spacing: .04em; padding: 3px 8px; border-radius: 999px;
  i { width: 5px; height: 5px; border-radius: 50%; background: currentColor; }
  &[data-tone="ok"] { background: rgba(40, 199, 111, .14); color: #1f9d57; }
  &[data-tone="wait"] { background: rgba(255, 159, 67, .14); color: #c77400; }
  &[data-tone="bad"] { background: rgba(234, 84, 85, .14); color: #ea5455; }
  &[data-tone="muted"] { background: rgba(100, 116, 139, .12); color: #64748b; }
}
.wtg-card__preview { margin: 0 -4px; }
.wtg-card__reason { font-size: .7rem; color: #b91c1c; background: rgba(234, 84, 85, .08); border-radius: 8px; padding: 6px 10px; }
.wtg-card__foot { display: flex; align-items: center; gap: 6px; margin-top: auto; }
.wtg-card__date { font-size: .66rem; color: #94a3b8; }
.wtg-action {
  width: 28px; height: 28px; border: 1px solid rgba(15, 23, 42, .08); border-radius: 9px; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center; background: #fff; color: #64748b; font-size: .72rem; transition: all .15s;
  &:hover { border-color: #7367f0; color: #7367f0; }
  &:focus { outline: none; }
  &--submit:hover { border-color: #28c76f; color: #1f9d57; }
  &--danger:hover { border-color: #ea5455; color: #ea5455; }
}
</style>

<style lang="scss">
.dark-layout {
  .wtg-seg { background: #283046; border-color: rgba(255, 255, 255, .12); }
  .wtg-seg__btn { color: #94a3b8; &--on { background: #7367f0; } }
  .wtg-tool { background: rgba(255, 255, 255, .06); color: #94a3b8; }
  .wtg-caps { background: rgba(37, 211, 102, .06); }
  .wtg-caps__head b { color: #e9ecf2; }
  .wtg-cap { background: #283046; border-color: rgba(255, 255, 255, .08); b { color: #e9ecf2; } span { color: #94a3b8; } }
  .wtg-card { background: #283046; border-color: rgba(255, 255, 255, .1); }
  .wtg-card__name { color: #e9ecf2; }
  .wtg-action { background: #283046; border-color: rgba(255, 255, 255, .12); color: #94a3b8; }
}
</style>
